import React, { useEffect, useMemo, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { toast } from "react-toastify";
import { MultiSelect } from "primereact/multiselect";
import * as apicall from "../../components/Apicall";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import * as XLSX from "xlsx";
import { LuFileSpreadsheet } from "react-icons/lu";
import { REASON_LABELS, STATUS_META, canonicalStatus, statusMeta } from "./leadMeta";
import { StatusPill, ChannelBadge, ReasonBadge, PriorityChip } from "./LeadPills";
import CreateLeadDrawer from "./CreateLeadDrawer";
import LeadDetailDrawer from "./LeadDetailDrawer";
import UnpaidBookingsView from "./UnpaidBookingsView";
import LeadKpiStrip from "./LeadKpiStrip";

const OPEN_STATUSES = ["NEW", "ASSIGNED", "IN_PROGRESS", "OPEN", "Follow-up"];

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

// Next follow-up is a date-only field (LeadDatePicker never collects a
// time) — formatDateTime above is for genuine timestamps (created_at,
// followup log entries) and would show a spurious 00:00/midnight-ish time
// component here since the stored value has no real time-of-day meaning.
const formatDateOnly = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : null;

// Maps a raw `leads` DB row (snake_case) to the shape the screen/components
// already render (camelCase) — keeps LeadPills/LeadDetailDrawer unchanged.
const mapApiLead = (row) => {
  const status = canonicalStatus(row.status);
  const overdue = !!row.next_followup_at && new Date(row.next_followup_at) < new Date() && OPEN_STATUSES.includes(status);
  return {
    id: row.id,
    isDraft: !!row.is_draft,
    bookingId: row.booking_id,
    ref: row.ref,
    customerName: row.customer_name,
    phone: row.phone,
    email: row.email,
    channel: row.source_type,
    reason: row.lead_reason,
    status,
    lastDisposition: row.last_disposition || null,
    assignedAgent: row.assigned_agent_name || null,
    assignedAgentId: row.assigned_agent_id,
    priority: row.priority,
    nextFollowUp: overdue ? "Overdue" : formatDateOnly(row.next_followup_at),
    nextFollowUpSub: overdue ? "past due" : "",
    overdue,
    service: row.service_name || "—",
    serviceType: row.service_type || null,
    serviceOffering: row.service_offering || null,
    cartValue: row.cart_value != null ? `₹${row.cart_value}` : "—",
    attempts: `${row.attempt_count || 0} of 6`,
    conversionChannel: row.conversion_channel,
    createdAt: formatDateTime(row.created_at),
    timeline: (row.followups || []).map((f) => ({
      id: f.id,
      kind: f.kind,
      who: f.kind === "system" ? "System" : f.kind === "auto" ? `${f.channel || "Automated"} nudge` : "Follow-up logged",
      when: formatDateTime(f.created_at),
      msg: f.message,
      disposition: f.disposition || undefined,
      deliveryStatus: f.delivery_status || undefined,
    })),
  };
};

// A row straight from get_user_pending_details (the exact same call
// UserFollowup.jsx makes) that has no ezp_leads row yet — shown as a
// "draft" so staff can see it and create a lead from it, same idea as
// UserFollowup's table always listing every pending record regardless of
// whether a follow-up had been logged for it yet.
const mapDraftRow = (row) => ({
  id: null,
  isDraft: true,
  bookingId: row.Booking_ID,
  ref: null,
  customerName: row.first_name,
  phone: row.phone_number,
  email: null,
  // Pending_order_details' proc DOES select bk.source as `channel` (verified
  // against the live proc definition) — pull it straight through rather than
  // hardcoding null, same as any other field this draft row already carries.
  channel: row.channel || null,
  reason: "payment_pending",
  status: null,
  assignedAgent: null,
  assignedAgentId: null,
  priority: null,
  nextFollowUp: null,
  nextFollowUpSub: "",
  overdue: false,
  service: row.service_name || "—",
  serviceType: row.service_type_name || null,
  serviceOffering: row.service_offering_name || null,
  cartValue: row.ServiceAmount_excl_taxes != null ? `₹${row.ServiceAmount_excl_taxes}` : "—",
  attempts: "0 of 6",
  conversionChannel: null,
  createdAt: formatDateTime(row.created),
  timeline: [],
});

// Same default-to-today pattern as UserFollowup.jsx in admin portal.
const today = new Date().toISOString().split("T")[0];

const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [reasonFilter, setReasonFilter] = useState([]);
  // Same "assigned to me by default, clearable to see everyone's" model as
  // the WhatsApp chat window's assignedToMe filter — defaults on for a team
  // member (role 6) so they land straight on their own follow-ups; the
  // vendor owner defaults to seeing everyone. Clearing it never grants edit
  // rights on someone else's lead — LeadDetailDrawer's own canManage gate
  console.log(leads, "leads");
  // still applies regardless of this filter.
  const [assignedToMe, setAssignedToMe] = useState(false);
  const [assignedToMeInitialized, setAssignedToMeInitialized] = useState(false);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;
  const [showCreate, setShowCreate] = useState(false);
  const [presetLead, setPresetLead] = useState(null);
  const [activeLeadId, setActiveLeadId] = useState(null);
  // Bumped after a lead is created or changed so the KPI tiles above the table re-read their numbers.
  const [summaryTick, setSummaryTick] = useState(0);
  const refreshSummary = () => setSummaryTick((t) => t + 1);

  // Who's looking at this screen: vendor owner / Support-team employees get
  // full Lead Management; everyone else under the vendor gets a plain
  // read-only unpaid-bookings view instead (rendered lower down, before any
  // of the lead-pipeline UI mounts).
  const [access, setAccess] = useState(null);
  const [accessLoading, setAccessLoading] = useState(true);

  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkAssignTo, setBulkAssignTo] = useState("");
  const [bulkFollowUp, setBulkFollowUp] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    apicall.get("leads/my-access")
      .then((res) => {
        const data = res?.data || { canManageLeads: false, canViewLeads: false };
        setAccess(data);
        // Team member (role 6) lands on "assigned to me" by default; the
        // vendor owner defaults to seeing everyone. Only set this once, the
        // first time access loads — never override a user's own toggle.
        if (!assignedToMeInitialized) {
          setAssignedToMe(Number(data.role) === 6);
          setAssignedToMeInitialized(true);
        }
      })
      .catch(() => setAccess({ canManageLeads: false, canViewLeads: false }))
      .finally(() => setAccessLoading(false));

    apicall.get("getVendorTeamUsers")
      .then((res) => setTeamMembers(res?.data || []))
      .catch(() => setTeamMembers([]));
  }, []);

  // activeLeadId is null both when nothing is selected AND, coincidentally,
  // as the id on every draft ("no lead yet") row — so `l.id === activeLeadId`
  // alone would match the first draft row instead of correctly finding
  // nothing, and the drawer could never actually close. Guard explicitly.
  const activeLead = useMemo(
    () => (activeLeadId == null ? null : leads.find((l) => l.id === activeLeadId) || null),
    [leads, activeLeadId]
  );

  const openCreateForLead = (draftLead) => {
    setPresetLead(draftLead);
    setShowCreate(true);
  };

  const fetchLeads = async () => {
    try {
      setLoading(true);

      // get_user_pending_details pulls stalled UtilStop bookings (Pending_order_details)
      // — that's UtilStop's own internal booking-stall funnel, not any real
      // vendor's own bookings, so it's only relevant for the UtilStop support
      // account (my_access's isUtilstopSupportAccount). Every other vendor's
      // list is driven entirely by their own ezp_leads rows, created manually
      // via Create Lead — starts empty, same update/WhatsApp flow either way
      // since none of that reads from the bookings table at all.
      const [pendingRes, leadsRes] = await Promise.all([
        access?.isUtilstopSupportAccount
          ? apicall.get(`get_user_pending_details?startDate=${startDate}&endDate=${endDate}`)
          : Promise.resolve({ data: [] }),
        apicall.get(`leads?startDate=${startDate}&endDate=${endDate}`),
      ]);

      const pendingRows = pendingRes?.data || [];
      const trackedLeads = (leadsRes?.data || []).map(mapApiLead);
      const leadsByBookingId = new Map(trackedLeads.filter((l) => l.bookingId).map((l) => [String(l.bookingId), l]));

      // Pending rows with no lead yet are shown as drafts ("no lead yet").
      // A pending row whose booking already has a lead is skipped here since
      // that lead is already included via trackedLeads below — matched by
      // booking_id, so the same customer stalling on two different bookings
      // always shows as two separate rows, never merged into one.
      const draftsFromPending = pendingRows
        .filter((row) => !leadsByBookingId.has(String(row.Booking_ID)))
        .map(mapDraftRow);

      // Every real lead is shown regardless of whether its booking still
      // appears in get_user_pending_details for the selected range — that
      // endpoint's Pending_order_details proc has its own notion of "still
      // pending" which can lag behind or diverge from whether a lead already
      // exists for it (e.g. the stale-payment-pending auto-create Lambda), so
      // an existing lead must never disappear from view because of that.
      let combined = [...trackedLeads, ...draftsFromPending];

      if (assignedToMe && Number(access?.role) === 6) {
        combined = combined.filter((l) => access?.userId != null && String(l.assignedAgentId) === String(access.userId));
      }
      if (statusFilter.length) combined = combined.filter((l) => statusFilter.includes(l.status));
      if (reasonFilter.length) combined = combined.filter((l) => reasonFilter.includes(l.reason));
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        combined = combined.filter(
          (l) => (l.customerName || "").toLowerCase().includes(q) || (l.phone || "").includes(q)
        );
      }

      setLeads(combined);
    } catch (err) {
      toast.error("Failed to load leads: " + err.message);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accessLoading || !access?.canViewLeads) return;
    const t = setTimeout(fetchLeads, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, reasonFilter, assignedToMe, startDate, endDate, accessLoading, access]);

  // Any filter change reshuffles what "page 1" means — always land back on
  // it, and drop any selection since the underlying row set changed.
  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [search, statusFilter, reasonFilter, assignedToMe, startDate, endDate]);

  const totalPages = Math.max(1, Math.ceil(leads.length / PAGE_SIZE));
  const pagedLeads = useMemo(
    () => leads.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [leads, page]
  );

  // Only real leads (not draft rows, which have no id) the current user can
  // actually manage can be bulk-selected — same gate as LeadDetailDrawer's
  // canManage: the vendor owner / Support team (access.canManageLeads), or
  // the lead's own assignee. Anyone else would just get silently skipped by
  // bulk_update_leads server-side, so don't offer the checkbox at all.
  const selectablePageIds = useMemo(
    () =>
      pagedLeads
        .filter(
          (l) =>
            l.id != null &&
            (access?.canManageLeads ||
              (access?.userId != null && String(l.assignedAgentId) === String(access.userId)))
        )
        .map((l) => l.id),
    [pagedLeads, access]
  );
  const selectablePageIdSet = useMemo(() => new Set(selectablePageIds), [selectablePageIds]);
  const allPageSelected = selectablePageIds.length > 0 && selectablePageIds.every((id) => selectedIds.includes(id));

  const toggleSelectAllOnPage = () => {
    if (allPageSelected) {
      setSelectedIds((ids) => ids.filter((id) => !selectablePageIds.includes(id)));
    } else {
      setSelectedIds((ids) => Array.from(new Set([...ids, ...selectablePageIds])));
    }
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  };

  // The whole table as it stands (every page, current filters and dates), one row per lead.
  const exportLeads = () => {
    const rows = leads.map((l) => ({
      "Lead Ref": l.ref || "",
      "Customer": l.customerName || "",
      "Mobile Number": l.phone || "",
      "Email": l.email || "",
      "Source": l.channel || "",
      "Lead Reason": REASON_LABELS[l.reason] || l.reason || "",
      "Status": l.isDraft ? "No lead yet" : (statusMeta(l.status).label || ""),
      "Disposition": l.lastDisposition || "",
      "Assigned Agent": l.assignedAgent || "",
      "Next Follow-up": l.overdue ? "Overdue" : (l.nextFollowUp || ""),
      "Priority": l.isDraft ? "" : (l.priority || ""),
      "Service": l.service && l.service !== "—" ? l.service : "",
      "Service Type": l.serviceType || "",
      "Service Offering": l.serviceOffering || "",
      "Cart Value": l.cartValue && l.cartValue !== "—" ? l.cartValue : "",
      "Attempts": l.isDraft ? "" : (l.attempts || ""),
      "Created": l.createdAt || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map((k) => ({ wch: Math.min(32, Math.max(k.length + 2, ...rows.map((r) => String(r[k]).length + 2))) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Leads");
    XLSX.writeFile(wb, `leads_${startDate}_to_${endDate}.xlsx`);
  };

  const handleCreate = async (form) => {
    try {
      const res = await apicall.post(
        {
          customer_name: form.customerName,
          phone: form.phone,
          email: form.email || null,
          lead_reason: form.reason || null,
          priority: form.priority,
          booking_id: form.pendingBookingId || null,
          assigned_agent_id: form.assignTo || null,
          note: form.note || null,
          service_name: form.serviceName || null,
          service_type: form.serviceType || null,
          service_offering: form.serviceOffering || null,
          cart_value: form.cartValue || null,
        },
        "leads"
      );
      if (!res?.success) {
        toast.error(res?.message || "Failed to create lead");
        return;
      }
      toast.success("Lead created successfully");
      setShowCreate(false);
      setPresetLead(null);
      fetchLeads();
      refreshSummary();
    } catch (err) {
      toast.error("Failed to create lead: " + err.message);
    }
  };

  const handleSaveUpdate = async (leadId, updates) => {
    try {
      const res = await apicall.post(
        {
          status: updates.status,
          disposition: updates.disposition || null,
          next_followup_at: updates.nextFollowUp || null,
          note: updates.note || null,
          ...(updates.assignedAgentId !== undefined ? { assigned_agent_id: updates.assignedAgentId || null } : {}),
          ...(updates.bookingIntent ? { booking_intent: updates.bookingIntent } : {}),
          // Only sent for Channels-sourced leads (see LeadDetailDrawer).
          ...(updates.bookingId !== undefined
            ? {
                booking_id: updates.bookingId || null,
                service_name: updates.serviceName || null,
                service_type: updates.serviceType || null,
                service_offering: updates.serviceOffering || null,
              }
            : {}),
        },
        `leads/${leadId}/update`
      );
      if (!res?.success) {
        toast.error(res?.message || "Failed to update lead");
        return;
      }
      setActiveLeadId(null);
      fetchLeads();
      refreshSummary();
    } catch (err) {
      toast.error("Failed to update lead: " + err.message);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.length === 0) return;
    try {
      setBulkSaving(true);
      const res = await apicall.post(
        {
          ids: selectedIds,
          status: bulkStatus || undefined,
          next_followup_at: bulkFollowUp || undefined,
          note: bulkNote || undefined,
          ...(bulkAssignTo !== "" ? { assigned_agent_id: bulkAssignTo || null } : {}),
        },
        "leads/bulk-update"
      );
      if (!res?.success) {
        toast.error(res?.message || "Failed to update leads");
        return;
      }
      const { updated, skipped } = res.data || {};
      if (skipped?.length) {
        toast.warn(`Updated ${updated} lead(s); ${skipped.length} skipped (not assigned to you)`);
      } else {
        toast.success(`Updated ${updated} lead(s)`);
      }
      setSelectedIds([]);
      setBulkStatus("");
      setBulkAssignTo("");
      setBulkFollowUp("");
      setBulkNote("");
      fetchLeads();
      refreshSummary();
    } catch (err) {
      toast.error("Failed to update leads: " + err.message);
    } finally {
      setBulkSaving(false);
    }
  };

  if (accessLoading) {
    return (
      <div style={{ padding: "60px 32px", textAlign: "center", color: "#9CA3AF" }}>Loading…</div>
    );
  }

  if (!access?.canViewLeads) {
    return <UnpaidBookingsView />;
  }

  return (
    <div style={{ padding: "28px 32px", minHeight: "100vh", background: "#F8FAFC" }}>

      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 22, color: "#111827", margin: "0 0 4px" }}>Lead Management</h1>
          <p style={{ fontSize: 13, color: "#6B7280", margin: 0 }}>
            Track and follow up on customers who didn't complete a booking — {leads.length} leads
          </p>
        </div>
        <button
          className="btn btn-danger"
          onClick={() => setShowCreate(true)}
          style={{ height: 40, borderRadius: 9, padding: "0 18px", display: "inline-flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: 13.5 }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"></path></svg>
          Create Lead
        </button>
      </div>

      <LeadKpiStrip
        startDate={startDate}
        endDate={endDate}
        refreshKey={summaryTick}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        rightAction={
          <button
            type="button"
            onClick={exportLeads}
            disabled={loading || leads.length === 0}
            title="Download every lead in the table (with the current filters) as an Excel sheet"
            style={{ height: 34, borderRadius: 8, padding: "0 14px", display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #D1D5DB", background: "#fff", color: "#1a3a5f", fontSize: 13, fontWeight: 600, cursor: loading || leads.length === 0 ? "default" : "pointer", opacity: loading || leads.length === 0 ? 0.5 : 1, whiteSpace: "nowrap" }}
          >
            <LuFileSpreadsheet size={16} color="#1D6F42" />
            Export
          </button>
        }
      />

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", padding: "14px 20px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "nowrap", whiteSpace: "nowrap", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <div style={{ position: "relative", flex: "1 1 200px", minWidth: 0 }}>
          <CiSearch style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", fontSize: 18, pointerEvents: "none" }} />
          <input
            type="text"
            placeholder="Search by customer name or phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "100%", height: 38, paddingLeft: 36, paddingRight: 12, border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 13, color: "#111827", outline: "none", background: "#F9FAFB" }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12.5, color: "#6B7280", fontWeight: 500 }}>Status</span>
          <MultiSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.value)}
            options={Object.keys(STATUS_META).map((s) => ({ label: STATUS_META[s].label, value: s }))}
            placeholder="All Status"
            showClear
            maxSelectedLabels={2}
            style={{ height: 38, fontSize: 13, minWidth: 150 }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12.5, color: "#6B7280", fontWeight: 500 }}>Lead Reason</span>
          <MultiSelect
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.value)}
            options={Object.entries(REASON_LABELS).map(([value, label]) => ({ label, value }))}
            placeholder="All Reasons"
            showClear
            maxSelectedLabels={2}
            style={{ height: 38, fontSize: 13, minWidth: 160 }}
          />
        </div>
        {Number(access?.role) === 6 && (
          <label
            style={{
              display: "flex", alignItems: "center", gap: 8, height: 38, padding: "0 12px",
              borderRadius: 8, cursor: "pointer", userSelect: "none",
              border: "1px solid " + (assignedToMe ? "#C9194A" : "#E5E7EB"),
              background: assignedToMe ? "#FFF1F4" : "#F9FAFB",
            }}
          >
            <input
              type="checkbox"
              checked={assignedToMe}
              onChange={(e) => setAssignedToMe(e.target.checked)}
              style={{ cursor: "pointer", accentColor: "#C9194A", width: 14, height: 14, margin: 0 }}
            />
            <span style={{ fontSize: 12.5, fontWeight: assignedToMe ? 600 : 500, color: assignedToMe ? "#C9194A" : "#6B7280" }}>
              Assigned to me
            </span>
          </label>
        )}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={{ fontSize: 12.5, color: "#6B7280", fontWeight: 500 }}>From</span>
          <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ height: 38, fontSize: 13, borderRadius: 8, border: "1px solid #E5E7EB", background: "#F9FAFB", width: 150 }} />
          <span style={{ fontSize: 12.5, color: "#6B7280", fontWeight: 500 }}>To</span>
          <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={{ height: 38, fontSize: 13, borderRadius: 8, border: "1px solid #E5E7EB", background: "#F9FAFB", width: 150 }} />
        </div>
        {(search || statusFilter.length > 0 || reasonFilter.length > 0 || startDate !== today || endDate !== today) && (
          <button
            onClick={() => { setSearch(""); setStatusFilter([]); setReasonFilter([]); setStartDate(today); setEndDate(today); }}
            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "#6B7280", cursor: "pointer", flexShrink: 0 }}
          >
            Clear
          </button>
        )}
      </div>

      {selectedIds.length > 0 && (
        <div style={{ background: "#FFF7ED", border: "1px solid #FDBA74", borderRadius: 12, padding: "12px 20px", marginBottom: 18, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#9A3412" }}>{selectedIds.length} selected</span>
          <AppDropdown
            value={bulkStatus}
            onChange={(e) => setBulkStatus(e.value)}
            options={Object.keys(STATUS_META).map((s) => ({ label: STATUS_META[s].label, value: s }))}
            placeholder="Status (no change)"
            style={{ height: 36, fontSize: 12.5, minWidth: 150 }}
          />
          <AppDropdown
            value={bulkAssignTo}
            onChange={(e) => setBulkAssignTo(e.value)}
            options={teamMembers.map((m) => ({ label: m.name || m.full_name, value: m.id }))}
            placeholder="Assign to (no change)"
            style={{ height: 36, fontSize: 12.5, minWidth: 170 }}
          />
          <input className="form-control" placeholder="Next follow-up (optional)" value={bulkFollowUp} onChange={(e) => setBulkFollowUp(e.target.value)} style={{ height: 36, fontSize: 12.5, borderRadius: 8, width: 170 }} />
          <input className="form-control" placeholder="Note (optional)" value={bulkNote} onChange={(e) => setBulkNote(e.target.value)} style={{ height: 36, fontSize: 12.5, borderRadius: 8, flex: "1 1 180px" }} />
          <button
            className="btn btn-danger"
            onClick={handleBulkUpdate}
            disabled={bulkSaving || (!bulkStatus && !bulkAssignTo && !bulkFollowUp && !bulkNote)}
            style={{ height: 36, borderRadius: 8, padding: "0 16px", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", opacity: bulkSaving ? 0.6 : 1 }}
          >
            Apply to {selectedIds.length}
          </button>
          <button
            onClick={() => setSelectedIds([])}
            style={{ background: "none", border: "none", color: "#9A3412", fontSize: 12.5, cursor: "pointer" }}
          >
            Clear selection
          </button>
        </div>
      )}

      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: 560 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1500 }}>
            <thead>
              <tr style={{ background: "#F8FAFC", borderBottom: "1px solid #E5E7EB", position: "sticky", top: 0, zIndex: 1 }}>
                <th style={{ padding: "12px 10px", borderRight: "1px solid #F3F4F6", width: 36 }}>
                  <input type="checkbox" checked={allPageSelected} onChange={toggleSelectAllOnPage} disabled={selectablePageIds.length === 0} />
                </th>
                {["Lead Ref", "Customer", "Mobile Number", "Source", "Lead Reason", "Status", "Disposition", "Assigned Agent", "Next Follow-up", "Priority", "Service", "Service Type", "Service Offering", "Action"].map((h) => (
                  <th key={h} style={{ padding: "12px 16px", textAlign: h === "Action" ? "center" : "left", fontWeight: 600, color: "#374151", fontSize: 11.5, textTransform: "uppercase", letterSpacing: "0.05em", whiteSpace: "nowrap", borderRight: "1px solid #F3F4F6" }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: "center", padding: "52px 0", color: "#9CA3AF" }}>
                    Loading…
                  </td>
                </tr>
              ) : leads.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: "center", padding: "52px 0", color: "#9CA3AF" }}>
                    <div style={{ fontWeight: 600, color: "#374151", marginBottom: 4 }}>No leads found</div>
                    <div style={{ fontSize: 12 }}>Try adjusting your search or filters</div>
                  </td>
                </tr>
              ) : (
                pagedLeads.map((lead) => (
                  <tr
                    key={lead.id || `draft-${lead.bookingId}`}
                    style={{ borderBottom: "1px solid #F3F4F6", cursor: "pointer" }}
                    onClick={() => (lead.isDraft ? openCreateForLead(lead) : setActiveLeadId(lead.id))}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAFA")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                  >
                    <td style={{ padding: "12px 10px", borderRight: "1px solid #F3F4F6" }} onClick={(e) => e.stopPropagation()}>
                      {selectablePageIdSet.has(lead.id) && (
                        <input type="checkbox" checked={selectedIds.includes(lead.id)} onChange={() => toggleSelectOne(lead.id)} />
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}>
                      {lead.ref
                        ? <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, color: "#C9194A", fontSize: 12.5 }}>{lead.ref}</span>
                        : <span style={{ color: "#C4C9D1", fontSize: 12.5, fontStyle: "italic" }}>Not created</span>}
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}>
                      <div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>{lead.customerName}</div>
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6", color: "#374151" }}>{lead.phone}</td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}><ChannelBadge channel={lead.channel} /></td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}><ReasonBadge reason={lead.reason} /></td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}>
                      {lead.isDraft
                        ? <span style={{ color: "#C4C9D1", fontSize: 12.5, fontStyle: "italic" }}>No lead yet</span>
                        : <StatusPill status={lead.status} />}
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6", fontSize: 12.5, color: "#374151", whiteSpace: "nowrap" }}>
                      {lead.lastDisposition || <span style={{ color: "#C4C9D1" }}>—</span>}
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}>
                      {lead.assignedAgent ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#EEF2FF", color: "#4F46E5", fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {lead.assignedAgent.split(" ").map((p) => p[0]).join("")}
                          </div>
                          {lead.assignedAgent}
                        </div>
                      ) : (
                        <span style={{ color: "#C4C9D1", fontSize: 12.5, fontStyle: "italic" }}>Unassigned</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}>
                      <div style={{ fontSize: 12.5, color: lead.overdue ? "#C9194A" : "#374151", fontWeight: lead.overdue ? 600 : 500 }}>
                        {lead.overdue ? "Overdue" : (lead.nextFollowUp || "—")}
                      </div>
                      <div style={{ fontSize: 11, color: "#9CA3AF" }}>{lead.nextFollowUpSub}</div>
                    </td>
                    <td style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6" }}>
                      {lead.isDraft ? <span style={{ color: "#C4C9D1" }}>—</span> : <PriorityChip priority={lead.priority} />}
                    </td>
                    {[lead.service, lead.serviceType, lead.serviceOffering].map((v, i) => (
                      <td key={i} style={{ padding: "12px 16px", borderRight: "1px solid #F3F4F6", fontSize: 12.5, color: "#374151", whiteSpace: "nowrap" }}>
                        {v && v !== "—" ? v : <span style={{ color: "#C4C9D1" }}>—</span>}
                      </td>
                    ))}
                    <td style={{ padding: "12px 16px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      {lead.isDraft ? (
                        <button
                          onClick={() => openCreateForLead(lead)}
                          title="Create Lead"
                          style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #C9194A", background: "#FFF1F4", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#C9194A", cursor: "pointer" }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"></path></svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => setActiveLeadId(lead.id)}
                          title="View"
                          style={{ width: 30, height: 30, borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#6B7280", cursor: "pointer" }}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px" }}>
          <div style={{ fontSize: 12.5, color: "#9CA3AF" }}>
            Showing {leads.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, leads.length)} of {leads.length} lead{leads.length !== 1 ? "s" : ""}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{ height: 32, padding: "0 14px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", color: page <= 1 ? "#C4C9D1" : "#374151", fontSize: 13, cursor: page <= 1 ? "default" : "pointer" }}
            >
              Previous
            </button>
            <span style={{ fontSize: 12.5, color: "#6B7280" }}>Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{ height: 32, padding: "0 14px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#fff", color: page >= totalPages ? "#C4C9D1" : "#374151", fontSize: 13, cursor: page >= totalPages ? "default" : "pointer" }}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <CreateLeadDrawer
        show={showCreate}
        onHide={() => { setShowCreate(false); setPresetLead(null); }}
        onCreate={handleCreate}
        presetLead={presetLead}
        access={access}
      />
      <LeadDetailDrawer
        show={!!activeLead}
        onHide={() => setActiveLeadId(null)}
        lead={activeLead}
        onSaveUpdate={handleSaveUpdate}
        access={access}
        teamMembers={teamMembers}
      />
    </div>
  );
};

export default LeadsList;
